import { supabase } from "@/integrations/supabase/client";
import type { Path } from "@/types/path";

/**
 * Deep-copy a published path into a new draft version.
 * This duplicates:
 * 1. The path record (new version, unpublished)
 * 2. All path_activity entries
 * 3. For each activity: deep-copy the activity + steps + transitions + cards/card_groups
 */
export async function deepCopyPathVersion(sourcePath: Path, userId: string): Promise<Path> {
  // 1. Create new path
  const { data: newPath, error: pathErr } = await supabase
    .from("path")
    .insert({
      title: sourcePath.title,
      path_type: sourcePath.path_type,
      theme_id: sourcePath.theme_id,
      metadata: sourcePath.metadata,
      resources: (sourcePath as any).resources,
      path_root_id: sourcePath.path_root_id || sourcePath.id,
      version: sourcePath.version + 1,
      is_published: false,
      created_by: userId,
      updated_by: userId,
    } as any)
    .select()
    .single();
  if (pathErr) throw pathErr;

  // 2. Get source path_activities
  const { data: sourcePAs } = await supabase
    .from("path_activity")
    .select("*")
    .eq("path_id", sourcePath.id)
    .order("order_index");

  if (!sourcePAs || sourcePAs.length === 0) return newPath;

  // 3. For each path_activity, deep-copy the activity
  for (const pa of sourcePAs) {
    const newActivityId = await deepCopyActivity(pa.activity_id, userId);

    const { error: paErr } = await supabase.from("path_activity").insert({
      path_id: newPath.id,
      activity_id: newActivityId,
      order_index: pa.order_index,
    });
    if (paErr) throw new Error(`Erreur liaison activité: ${paErr.message}`);
  }

  return newPath;
}

/** Recursively remap card_id and card_group_id references in transition conditions */
function remapConditionIds(
  condition: Record<string, unknown>,
  cardMap: Map<string, string>,
  groupMap: Map<string, string>,
): Record<string, unknown> {
  const cType = (condition as any).type;

  if (cType === "compound") {
    const subConditions = ((condition as any).conditions || []).map((sub: any) =>
      remapConditionIds(sub, cardMap, groupMap)
    );
    return { ...condition, conditions: subConditions };
  }

  const result = { ...condition };

  // Remap card_id
  if ((result as any).card_id) {
    result.card_id = cardMap.get((result as any).card_id) || (result as any).card_id;
  }
  // Remap card_group_id
  if ((result as any).card_group_id) {
    result.card_group_id = groupMap.get((result as any).card_group_id) || (result as any).card_group_id;
  }
  // Remap card_id_a / card_id_b
  if ((result as any).card_id_a) {
    result.card_id_a = cardMap.get((result as any).card_id_a) || (result as any).card_id_a;
  }
  if ((result as any).card_id_b) {
    result.card_id_b = cardMap.get((result as any).card_id_b) || (result as any).card_id_b;
  }
  // Remap card_group_id_a / card_group_id_b
  if ((result as any).card_group_id_a) {
    result.card_group_id_a = groupMap.get((result as any).card_group_id_a) || (result as any).card_group_id_a;
  }
  if ((result as any).card_group_id_b) {
    result.card_group_id_b = groupMap.get((result as any).card_group_id_b) || (result as any).card_group_id_b;
  }

  return result;
}

/**
 * Deep-copy an activity: creates a new draft version with all steps, transitions,
 * cards, and card_groups duplicated.
 */
async function deepCopyActivity(activityId: string, userId: string): Promise<string> {
  // Fetch source activity
  const { data: source, error } = await supabase
    .from("activity")
    .select("*")
    .eq("id", activityId)
    .single();
  if (error) throw error;

  // Create new activity
  const { data: newActivity, error: insertErr } = await supabase
    .from("activity")
    .insert({
      title: source.title,
      summary: source.summary,
      estimated_duration_minutes: source.estimated_duration_minutes,
      metadata: source.metadata,
      activity_root_id: source.activity_root_id || source.id,
      version: source.version + 1,
      is_published: false,
      created_by: userId,
      updated_by: userId,
    })
    .select()
    .single();
  if (insertErr) throw insertErr;

  // Fetch steps
  const { data: oldSteps } = await supabase
    .from("activity_step")
    .select("*")
    .eq("activity_id", activityId);

  if (!oldSteps || oldSteps.length === 0) return newActivity.id;

  const oldToNewStepId = new Map<string, string>();
  const oldToNewCardId = new Map<string, string>();
  const oldToNewGroupId = new Map<string, string>();

  for (const step of oldSteps) {
    let newCardId: string | null = null;
    let newGroupId: string | null = null;

    if (step.card_id) {
      newCardId = await deepCopyCard(step.card_id, userId);
      oldToNewCardId.set(step.card_id, newCardId);
    } else if (step.card_group_id) {
      newGroupId = await deepCopyCardGroup(step.card_group_id, userId, oldToNewCardId);
      oldToNewGroupId.set(step.card_group_id, newGroupId);
    }

    const { data: newStep, error: stepErr } = await supabase
      .from("activity_step")
      .insert({
        activity_id: newActivity.id,
        card_id: newCardId,
        card_group_id: newGroupId,
      })
      .select("id")
      .single();
    if (stepErr) throw new Error(`Erreur copie étape: ${stepErr.message}`);
    if (newStep) oldToNewStepId.set(step.id, newStep.id);
  }

  // Copy transitions
  for (const oldStep of oldSteps) {
    const { data: transitions } = await supabase
      .from("activity_step_transition")
      .select("*")
      .eq("from_activity_step_id", oldStep.id);

    if (transitions) {
      for (const t of transitions) {
        const newFrom = oldToNewStepId.get(t.from_activity_step_id);
        const newTo = oldToNewStepId.get(t.to_activity_step_id);
        if (newFrom && newTo) {
          // Remap card_id references in conditions
          let condition = t.condition as Record<string, unknown> | null;
          if (condition) {
            condition = remapConditionIds(condition, oldToNewCardId, oldToNewGroupId);
          }
          const { error: transErr } = await supabase.from("activity_step_transition").insert({
            from_activity_step_id: newFrom,
            to_activity_step_id: newTo,
            condition: condition as any,
            order_index: t.order_index,
          });
          if (transErr) throw new Error(`Erreur copie transition: ${transErr.message}`);
        }
      }
    }
  }

  // Remap metadata.nodePositions and first_step_id
  const updates: Record<string, unknown> = {};

  if (source.first_step_id && oldToNewStepId.has(source.first_step_id)) {
    updates.first_step_id = oldToNewStepId.get(source.first_step_id);
  }

  const meta = (source.metadata as Record<string, unknown>) || {};
  if (meta.nodePositions && typeof meta.nodePositions === "object") {
    const oldPositions = meta.nodePositions as Record<string, { x: number; y: number }>;
    const newPositions: Record<string, { x: number; y: number }> = {};
    for (const [oldId, pos] of Object.entries(oldPositions)) {
      const newId = oldToNewStepId.get(oldId);
      if (newId) newPositions[newId] = pos;
    }
    updates.metadata = { ...meta, nodePositions: newPositions };
  }

  if (Object.keys(updates).length > 0) {
    const { error: updErr } = await supabase
      .from("activity")
      .update(updates as any)
      .eq("id", newActivity.id);
    if (updErr) throw new Error(`Erreur mise à jour activité: ${updErr.message}`);
  }

  return newActivity.id;
}

async function deepCopyCard(cardId: string, userId: string): Promise<string> {
  const { data: source, error } = await supabase
    .from("card")
    .select("*")
    .eq("id", cardId)
    .single();
  if (error) throw error;

  const { data: newCard, error: insertErr } = await supabase
    .from("card")
    .insert({
      card_template_id: source.card_template_id,
      content: source.content,
      tooltips: source.tooltips,
      estimated_duration_minutes: source.estimated_duration_minutes,
      card_root_id: source.card_root_id || source.id,
      version: source.version + 1,
      created_by: userId,
      updated_by: userId,
    } as any)
    .select("id")
    .single();
  if (insertErr) throw insertErr;
  return newCard.id;
}

async function deepCopyCardGroup(groupId: string, userId: string, oldToNewCardId?: Map<string, string>): Promise<string> {
  const { data: source, error } = await supabase
    .from("card_group")
    .select("*")
    .eq("id", groupId)
    .single();
  if (error) throw error;

  const { data: newGroup, error: insertErr } = await supabase
    .from("card_group")
    .insert({
      title: source.title,
      group_type: source.group_type,
      config: source.config,
      card_group_root_id: source.card_group_root_id || source.id,
      version: source.version + 1,
      created_by: userId,
      updated_by: userId,
    })
    .select("id")
    .single();
  if (insertErr) throw insertErr;

  // Copy group items
  const { data: items } = await supabase
    .from("card_group_item")
    .select("*")
    .eq("card_group_id", groupId)
    .order("order_index");

  if (items) {
    for (const item of items) {
      const newCardId = await deepCopyCard(item.card_id, userId);
      oldToNewCardId?.set(item.card_id, newCardId);
      const { error: giErr } = await supabase.from("card_group_item").insert({
        card_group_id: newGroup.id,
        card_id: newCardId,
        order_index: item.order_index,
      });
      if (giErr) throw new Error(`Erreur copie groupe item: ${giErr.message}`);
    }
  }

  return newGroup.id;
}
